<template>
  <div class="page-container">
    <PageHeader title="日志活跃统计">
      <template #actions>
        <el-button v-if="!showChart" type="success" size="small" @click="toggleView(true)">柱状图</el-button>
        <el-button v-if="showChart" type="success" size="small" @click="toggleView(false)">表格</el-button>
      </template>
    </PageHeader>

    <SearchPanel>
      <el-date-picker v-model="query.slsYear" type="year" value-format="yyyy" @change="handleSearch"
        placeholder="选择年" size="small" style="width: 140px"></el-date-picker>
      <el-date-picker v-model="query.slsMonth" type="month" value-format="yyyyMM" @change="handleSearch"
        placeholder="选择月" size="small" style="width: 140px"></el-date-picker>
      <el-date-picker v-model="query.slsDay" type="date" value-format="yyyyMMdd" @change="handleSearch"
        placeholder="选择日" size="small" style="width: 160px"></el-date-picker>
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <!-- 柱状图视图 -->
    <TablePanel v-if="showChart">
      <div ref="eCharts1" style="width: 100%; height: 500px;"></div>
    </TablePanel>

    <!-- 表格视图 -->
    <TablePanel v-if="!showChart">
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 40, 80, 160, 320]"
        :total="total"
        row-id-key="slsId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
      >
        <template #cell-slsStatus="{ row }">
          <el-tag :type="row.slsStatus === 1 ? 'danger' : 'success'" size="mini">{{ row.slsStatus === 1 ? '待处理' : '已处理' }}</el-tag>
        </template>
        <template #cell-operation="{ row }">
          <div class="action-btns">
            <el-button type="text" size="small" @click="handleDetail(row)">时间段</el-button>
          </div>
        </template>
      </aj-table>
      <EmptyState v-if="!loading && list.length === 0" title="暂无统计数据" icon="el-icon-data-line"></EmptyState>
    </TablePanel>

    <!-- 时间段详情弹窗 -->
    <el-dialog v-dialog-drag-resize title="活跃用户时间段分布" :visible.sync="diaDetail" width="80%" :close-on-click-modal="false"
      :before-close="handleDetailClose">
      <div ref="eCharts2" style="width: 100%; height: 500px;"></div>
    </el-dialog>
  </div>
</template>

<script>
import { getSysLogStatisticsList, getSysLogStatisticsDetails } from '@/api/api'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

// 统计表格列
const STATS_COLUMNS = [
  { type: 'index', label: '序号', width: 60 },
  { prop: 'slsYear', label: '年份'},
  { prop: 'slsDate', label: '日期', },
  { prop: 'slsUserNum', label: '活跃用户数量',  },
  { prop: 'slsLogNum', label: '日志数量',  },
  { prop: 'slsStatus', label: '状态',},
  { type: 'operation', prop: 'operation', label: '操作', fixed: 'right' }
]

// 渐变色配置
const GRADIENT_COLORS = [
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#a8c8ff' }, { offset: 1, color: '#73a6ff' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#a8f0d0' }, { offset: 1, color: '#73d1a4' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#d1c4e9' }, { offset: 1, color: '#b39ddb' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#fff59d' }, { offset: 1, color: '#fff176' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#ffccd5' }, { offset: 1, color: '#ff9aa2' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#c8e6c9' }, { offset: 1, color: '#a5d6a7' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#b3e5fc' }, { offset: 1, color: '#81d4fa' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#e1bee7' }, { offset: 1, color: '#ce93d8' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#fff9c4' }, { offset: 1, color: '#fff59d' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#f8bbd0' }, { offset: 1, color: '#f48fb1' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#dcedc8' }, { offset: 1, color: '#c5e1a5' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#bbdefb' }, { offset: 1, color: '#90caf9' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#a7ffeb' }, { offset: 1, color: '#64ffda' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#fff8e1' }, { offset: 1, color: '#ffecb3' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#e0f7fa' }, { offset: 1, color: '#b2ebf2' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#f3e5f5' }, { offset: 1, color: '#e1bee7' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#ede7f6' }, { offset: 1, color: '#d1c4e9' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#fff3e0' }, { offset: 1, color: '#ffe0b2' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#e8f5e9' }, { offset: 1, color: '#c8e6c9' }] },
  { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#e0f2f1' }, { offset: 1, color: '#b2dfdb' }] }
]

export default {
  name: 'LogActivityStatisticsPage',
  components: { AjTable, EmptyState, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      total: 0,
      list: [],
      columns: STATS_COLUMNS,
      query: { slsYear: '', slsMonth: '', slsDay: '', currentPage: 1, pageSize: 10 },
      showChart: false,
      diaDetail: false,
      detailData: [],
      echartsStr: null,
      echartsStr2: null
    }
  },
  created () { this.fetchList() },
  mounted () {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const params = {
          slsYear: this.query.slsYear || undefined,
          slsMonth: this.query.slsMonth || undefined,
          slsDay: this.query.slsDay || undefined,
          currentPage: this.query.currentPage,
          pageSize: this.query.pageSize
        }
        const res = await getSysLogStatisticsList(params)
        this.list = Array.isArray(res.data) ? res.data : []
        this.total = Number(res.total || 0)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    // 切换表格/柱状图
    toggleView (showChart) {
      this.showChart = showChart
      if (showChart) {
        this.$nextTick(() => this.drawChart())
      } else {
        if (this.echartsStr) { this.echartsStr.dispose(); this.echartsStr = null }
      }
    },
    // 查看时间段详情
    async handleDetail (row) {
      try {
        const res = await getSysLogStatisticsDetails({ slsId: row.slsId })
        this.detailData = Array.isArray(res.data) ? res.data : []
        this.diaDetail = true
        this.$nextTick(() => this.drawChart2())
      } catch (e) { /* ignore */ }
    },
    handleDetailClose () {
      this.diaDetail = false
      this.detailData = []
      if (this.echartsStr2) { this.echartsStr2.dispose(); this.echartsStr2 = null }
    },
    handleResize () {
      this.$nextTick(() => {
        if (this.echartsStr) this.echartsStr.resize()
        if (this.echartsStr2) this.echartsStr2.resize()
      })
    },
    // 构建图表配置
    buildChartOption (dataX, dataY, dataZ, barWidth) {
      return {
        title: { text: '', left: 'center' },
        legend: { top: '10px', left: 'center', textStyle: { fontSize: 12 }, data: ['人数', '日志数量'] },
        tooltip: {
          trigger: 'axis', axisPointer: { type: 'shadow' },
          backgroundColor: 'rgba(255,255,255,0.9)', textStyle: { color: '#333' },
          formatter: (params) => {
            return `<div style="font-weight:bold;">${params[0].name}</div>
              <div>人数: <span style="color:${params[0].color}">${params[0].value}人</span></div>
              <div>日志数量: <span>${params[1] ? params[1].value : '0'}</span></div>`
          }
        },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: {
          type: 'category', data: dataX,
          axisTick: { alignWithLabel: true },
          axisLabel: { color: '#666', fontSize: 12, interval: 0, rotate: dataX.length > 15 ? 90 : 0 }
        },
        yAxis: [
          { type: 'value', name: '人数', min: 0, axisLabel: { color: '#666' }, splitLine: { lineStyle: { color: '#eee' } } },
          { type: 'value', name: '日志数量', min: 0, axisLabel: { color: '#666' }, splitLine: { show: false } }
        ],
        series: [
          {
            name: '人数', type: 'bar', barWidth: barWidth || '20%',
            data: dataY,
            label: { show: true, position: 'top', formatter: '{c}人', color: '#333' },
            itemStyle: {
              borderRadius: [8, 8, 0, 0],
              color: (params) => GRADIENT_COLORS[params.dataIndex % GRADIENT_COLORS.length]
            }
          },
          {
            name: '日志数量', type: 'line', yAxisIndex: 1,
            data: dataZ,
            lineStyle: { width: 3 },
            itemStyle: { borderWidth: 2 },
            label: { show: true, position: 'top', color: 'red', distance: 10 },
            symbol: 'circle', symbolSize: 8
          }
        ]
      }
    },
    // 绘制主图表
    drawChart () {
      const dataX = this.list.map(i => i.slsDay)
      const dataY = this.list.map(i => i.slsUserNum)
      const dataZ = this.list.map(i => i.slsLogNum)
      this.echartsStr = this.$echarts.init(this.$refs.eCharts1)
      this.echartsStr.setOption(this.buildChartOption(dataX, dataY, dataZ, '20%'))
    },
    // 绘制时间段详情图表
    drawChart2 () {
      const dataX = this.detailData.map(i => (i.slsTime < 10 ? '0' + i.slsTime : i.slsTime) + ':00')
      const dataY = this.detailData.map(i => i.slsUserNum)
      const dataZ = this.detailData.map(i => i.slsLogNum)
      this.echartsStr2 = this.$echarts.init(this.$refs.eCharts2)
      this.echartsStr2.setOption(this.buildChartOption(dataX, dataY, dataZ, '40%'))
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery () {
      this.query = { slsYear: '', slsMonth: '', slsDay: '', currentPage: 1, pageSize: 10 }
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.action-btns {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
</style>
